/**
 * Entitlement constants for Idle Depths DLC system.
 *
 * Defines all purchasable content and features that can be
 * unlocked through the entitlement system.
 *
 * @see docs/design/monetization.md for full monetization strategy
 */

/**
 * All entitlement keys in the system.
 *
 * Use these constants instead of string literals for type safety.
 */
export const ENTITLEMENTS = {
  /**
   * Base game - always granted to all users.
   * Includes first 3-4 areas (~60% of content).
   */
  BASE_GAME: 'base_game',

  /**
   * Tier 1: Premium Unlock (one-time purchase).
   * Unlocks:
   * - All areas
   * - Premium skill tree branches
   * - Highscores/leaderboards
   * - Analytics tools
   * - Ad removal
   * - Mod support
   */
  PREMIUM_UNLOCK: 'premium_unlock',

  /**
   * Tier 2: Dev Support Package (one-time purchase).
   * Includes:
   * - Cosmetic bundle (skins, effects, UI themes)
   * - Soundtrack download
   * - Digital artbook
   * - Supporter badge
   * - Discord role
   * - Credits listing
   */
  DEV_SUPPORT: 'dev_support',

  /**
   * Tier 3: Convenience Package (one-time purchase).
   * Quality-of-life features:
   * - Auto-replant
   * - Auto-new-request
   * - Item alerts
   * - Auto-sell
   * - Auto-open
   * - Auto-bury
   * - Bank presets
   */
  CONVENIENCE: 'convenience',

  /**
   * Developer Tools - for internal development and testing.
   * Provides access to debug panels and developer utilities.
   * Should only be granted to developers/testers via direct database access.
   * This is NOT the same as DEV_SUPPORT (purchasable cosmetic pack).
   */
  DEV: 'dev',
} as const;

/**
 * Type for entitlement keys.
 */
export type EntitlementKey = (typeof ENTITLEMENTS)[keyof typeof ENTITLEMENTS];

/**
 * All valid entitlement keys as an array.
 * Useful for iteration and validation.
 */
export const ALL_ENTITLEMENT_KEYS: EntitlementKey[] = Object.values(ENTITLEMENTS);

/**
 * Purchase sources for entitlements.
 */
export const ENTITLEMENT_SOURCES = {
  /** Purchased through web payment (Stripe, PayPal) */
  WEB: 'web',

  /** Purchased through Steam DLC system */
  STEAM: 'steam',

  /** Purchased through iOS App Store */
  IOS: 'ios',

  /** Purchased through Google Play Store */
  ANDROID: 'android',

  /** Granted by admin (beta testers, support, etc.) */
  ADMIN: 'admin',
} as const;

/**
 * Type for entitlement sources.
 */
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[keyof typeof ENTITLEMENT_SOURCES];

/**
 * Checks if a string is a valid entitlement key.
 *
 * @param key - String to validate
 * @returns True if key is a valid EntitlementKey
 */
export function isValidEntitlementKey(key: string): key is EntitlementKey {
  return ALL_ENTITLEMENT_KEYS.includes(key as EntitlementKey);
}

/**
 * Entitlements that are granted to all users by default.
 */
export const DEFAULT_ENTITLEMENTS: EntitlementKey[] = [ENTITLEMENTS.BASE_GAME];
